// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/checkProgressRows.test.ts
import {
  type AskPunttCheckProgress,
  type AskPunttMessage,
} from "@mg/schemas/src/christo/askPuntt";

import { humanizeCheckName } from "./checkNames";
import { type AskV2RailProgressItem } from "./types";

/**
 * The row id prefix, so a check row can never collide with a `plan-` row.
 */
const CHECK_ROW_PREFIX = "check";

/**
 * The label a check row draws, from the check's warhol name.
 *
 * Passed in rather than imported so this module stays i18n-free: the rail's
 * copy is translated where `t` lives (`useAskV2PlanProgress`), the same
 * division `nestCheckProgress`'s `verifiedDetail` runs on.
 */
export type CheckLabel = (name: string) => string;

/**
 * How bad each rail status is, for folding a repeated check into one row.
 *
 * `failed` is worst because it is the one that must never be hidden: a check
 * that half-ran is not a check that passed. `blocked` (a `skipped` leg) is
 * better than `failed` and worse than `done`, since it means the work did not
 * happen at all rather than that it happened and found something.
 */
const CHECK_STATUS_RANK: Record<AskV2RailProgressItem["status"], number> = {
  done: 0,
  blocked: 1,
  failed: 2,
  // Nothing that builds a check row produces these; ranked so the map is total.
  pending: 0,
  active: 0,
};

/**
 * warhol's one verifier (`agents/artwork/evidence.py`). It is the roster name
 * an accepted or rejected verdict comes back under, which is what the
 * "Verified k of n" line counts.
 */
export const ASK_V2_CHECK_VERIFIER_NAME = "evidence-verifier";

/**
 * The rail status a warhol check status draws as.
 *
 * `failed` and `timed_out` both mean the check did not do the work, and both
 * must reach the rail as a failure — a green tick on a timed-out reviewer is
 * the exact lie `P0-askv2-rail-ticks-from-write-plan` removed the timers for.
 *
 * `skipped` is a check that could NOT run — every spec-only check on a review
 * with no spec attached — and it draws as `blocked`, carrying its reason as
 * the row's `detail`. It is the one producer `AskV2RightRail`'s blocked glyph
 * ever had: `roberto.faga/0033` put the row in for it, and the live channel
 * that publishes the entry is `checkProgress` (`ROBERTO 2026-10-14`, restoring
 * what #5686 deleted). The thread's own chip reads the same entry the same way
 * — `rowTick` (`routes/ask-puntt/components/SubagentToolStep.tsx`) answers
 * `blocked` with the skip's reason — and the two surfaces describe one truth.
 *
 * It is NOT drawn as `done`, which is what this function did before the
 * restore: a struck-through tick beside work that never happened claims the
 * check passed. It is not `failed` either, because a skip is not a failure.
 */
export function railStatusForCheck(
  status: AskPunttCheckProgress["status"],
): AskV2RailProgressItem["status"] {
  switch (status) {
    case "done":
      return "done";
    case "failed":
    case "timed_out":
      return "failed";
    case "skipped":
      return "blocked";
    default:
      return "done";
  }
}

/**
 * One rail row per check the running turn has settled, in finish order.
 *
 * Keyed by NAME, not by entry: a check is one thing to the person however many
 * legs it runs as. `identifier-reader` is spawned twice in one `run_subagents`
 * call -- once for the spec, once for the artwork (`agents/skills/detcheck.py`)
 * -- and `evidence-verifier` once per intent, which on a real review is dozens;
 * drawn per entry, the rail showed `identifier-reader` three times in a row and
 * buried the plan under verifier rows (Roberto, 2026-10-16). The thread's own
 * chip already reads the list this way (`routes/ask-puntt/components/
 * SubagentToolStep.tsx::rowTick` filters by `name`), so this is the rail
 * catching up with the surface beside it rather than a new rule.
 *
 * A leg that repeats can settle with DIFFERENT statuses -- a retry that
 * succeeded after a failure, one reading failed of two -- so the row takes the
 * WORST of them: `failed` beats `blocked` beats `done`, because a check that
 * partially did not do the work must not draw a tick. The reason rides from the
 * entry that set that worst status.
 *
 * The label comes from the passed-in `labelFor`, defaulting to
 * `humanizeCheckName`: warhol's names are its own (`barcode_zone`,
 * `identifier-reader`) and a raw one must never reach a person.
 */
export function checkProgressRows(
  checkProgress: readonly AskPunttCheckProgress[] | undefined,
  labelFor: CheckLabel = humanizeCheckName,
): AskV2RailProgressItem[] {
  const rows: AskV2RailProgressItem[] = [];
  // Name -> its row's index, so a repeat folds into the row already drawn
  // instead of adding a second one the person has to read twice.
  const indexByName = new Map<string, number>();
  for (const entry of checkProgress ?? []) {
    const status = railStatusForCheck(entry.status);
    const seen = indexByName.get(entry.name);
    if (seen === undefined) {
      indexByName.set(entry.name, rows.length);
      rows.push({
        id: `${CHECK_ROW_PREFIX}-${entry.name}`,
        label: labelFor(entry.name),
        ...(entry.reason ? { detail: entry.reason } : {}),
        status,
      });
      continue;
    }
    const row = rows[seen];
    if (!row) continue;
    const worse = CHECK_STATUS_RANK[status] > CHECK_STATUS_RANK[row.status];
    rows[seen] = {
      ...row,
      status: worse ? status : row.status,
      // The reason of the leg that set the status, and never a silent
      // overwrite: a row with no reason of its own may adopt one.
      ...((worse || !row.detail) && entry.reason
        ? { detail: entry.reason }
        : {}),
    };
  }
  return rows;
}

/**
 * How many of this turn's verifiers have finished, or null when none has run.
 *
 * `total` counts the verifier legs warhol has PUBLISHED, not the intents the
 * turn means to verify: warhol publishes a leg only when it settles, so a
 * verifier still running is not on the list and could not be counted, and
 * every published leg that is not a failure is `done`. The ratio is therefore
 * a running tally of settled work, never a completion bar — `total` grows with
 * `verified`. The copy says "so far" for exactly that reason: without it a
 * six-verifier turn reads "Verified 6 of 6" while five are still running,
 * which claims a review is finished when it is not.
 *
 * A true denominator needs warhol to publish how many verifiers a turn will
 * run. It does not today, and inventing one here is worse than a numerator
 * that rises.
 */
export function verifierTally(
  checkProgress: readonly AskPunttCheckProgress[] | undefined,
): { verified: number; total: number } | null {
  const verifiers = (checkProgress ?? []).filter(
    (entry) => entry.name === ASK_V2_CHECK_VERIFIER_NAME,
  );
  if (verifiers.length === 0) return null;
  return {
    verified: verifiers.filter((entry) => entry.status === "done").length,
    total: verifiers.length,
  };
}

/**
 * The checks the newest message in a review cycle carries, or nothing.
 *
 * Read off the LAST assistant message that has any, rather than off the last
 * message: the placeholder row is replaced in place when the answer lands and
 * the answer carries no `checkProgress`, so a cycle that has just finished has
 * none to give — but a cycle still waiting does, on its pending row.
 */
export function checkProgressFromMessages(
  messages: readonly AskPunttMessage[] | undefined,
): AskPunttCheckProgress[] {
  for (let index = (messages?.length ?? 0) - 1; index >= 0; index -= 1) {
    const entries = messages?.[index]?.checkProgress;
    if (entries?.length) return entries;
  }
  return [];
}

/**
 * The same rows, with the running turn's settled checks nested under the row
 * it is working on.
 *
 * Nests under the `active` row, or under the last row when nothing is active —
 * a plan whose steps are all `pending` while checks settle is a plan the model
 * has not advanced yet, and the checks still belong on the rail. An empty list
 * returns `items` untouched, so a turn with nothing settled is byte-identical
 * to what this rail drew before checks existed.
 */
export function nestCheckProgress(
  items: readonly AskV2RailProgressItem[],
  checkProgress: readonly AskPunttCheckProgress[] | undefined,
  verifiedDetail: (tally: { verified: number; total: number }) => string,
  labelFor: CheckLabel = humanizeCheckName,
): AskV2RailProgressItem[] {
  const children = checkProgressRows(checkProgress, labelFor);
  if (children.length === 0 || items.length === 0) return [...items];
  const activeIndex = items.findIndex((item) => item.status === "active");
  const isActiveRow = activeIndex >= 0;
  const target = isActiveRow ? activeIndex : items.length - 1;
  const tally = verifierTally(checkProgress);
  // The tally is the ACTIVE row's line, and only when the row has none of its
  // own. Falling back to the last row put it — and the checks under it — on a
  // step the model had withdrawn, and overwrote that row's own `skipped`
  // marker with a count of work it never did.
  const detail = isActiveRow && tally ? verifiedDetail(tally) : undefined;
  return items.map((item, index) =>
    index === target
      ? {
          ...item,
          ...(detail && !item.detail ? { detail } : {}),
          children,
        }
      : item,
  );
}
